import { BBOT_DJANGO_SERVER } from "Config";
import { BbotButton, PageLoadingSpinner } from "top-component-library";
import { CardStack, DetailHeader, ReadOnlyBanner } from "../styles";
import AdvancedOptionsSection from "./AdvancedOptionsSection";
import BosBuilderCard from "./BosBuilderCard";
import LocationAssignmentSection from "./LocationAssignmentSection";
import MenuOnlyBosCard from "./MenuOnlyBosCard";

// Mirrors edit-hostname-configs.html's "Edit HostnameConfig view".
const HostnameConfigDetail = ({
  hostnameConfig,
  isAdmin,
  isBbotAdmin,
  customerName,
  onBack,
  onSave,
  onEnableLegacyBos,
  onArchiveHostname,
  onEnableBasicDeliveryTheme,
  onEnableWhiteLabelBuilder,
  onTemporarilyDisableOrdering,
  fetchTemporaryDisableText,
  locationsByCustomer,
  locationCustomers,
  fulfillmentMethods,
  prettyNames,
  isLoadingLocations,
  isSavingLocations,
  onSaveHostnameLocations,
}) => {
  // Deliberately not gated on the hook's isLoading: hostnameConfigs (and so
  // hostnameConfig) holds the previous fetch's data until a refresh fully
  // resolves, so this stays populated across a save's refresh. Gating on
  // isLoading here caused every save to swap this whole subtree out for a
  // spinner mid-flight, dropping local dialog state and unmounting components
  // with in-flight promises.
  if (!hostnameConfig) return <PageLoadingSpinner />;

  const bosBuilderUrl = `${BBOT_DJANGO_SERVER}/admin/bos-builder/?hostname=${hostnameConfig.hostname}`;

  // Mirrors showArchiveBOSDialog()'s history.back() after a successful archive -
  // the hostname disappears from the refreshed list, so staying on its now-defunct
  // detail view would otherwise strand the user on an infinite loading spinner.
  const archiveHostnameAndGoBack = async (hostname) => {
    const succeeded = await onArchiveHostname(hostname);
    if (succeeded) onBack();
    return succeeded;
  };

  return (
    <div>
      {!hostnameConfig.canEdit && (
        <ReadOnlyBanner
          type="info"
          message={
            <span>
              You are currently in read-only mode because this domain is owned by a parent account. To request changes,
              please contact the owner of the domain
              {isAdmin ? " or switch to the parent customer's owner panel" : ""}.
            </span>
          }
        />
      )}
      <DetailHeader>
        <BbotButton onClick={onBack}>Back</BbotButton>
        <h2>
          {hostnameConfig.hostname} Settings {!hostnameConfig.canEdit && <span className="f-500">(read-only)</span>}
        </h2>
      </DetailHeader>
      <p>
        View branded ordering site configuration options and enable one here. Each domain can have a different
        configuration.
      </p>
      <CardStack>
        {isAdmin && (
          <MenuOnlyBosCard hostnameConfig={hostnameConfig} onEnableBasicDeliveryTheme={onEnableBasicDeliveryTheme} />
        )}
        {isAdmin && (
          <BosBuilderCard
            hostnameConfig={hostnameConfig}
            bosBuilderUrl={bosBuilderUrl}
            onEnableWhiteLabelBuilder={onEnableWhiteLabelBuilder}
          />
        )}
        <AdvancedOptionsSection
          hostnameConfig={hostnameConfig}
          isAdmin={isAdmin}
          isBbotAdmin={isBbotAdmin}
          customerName={customerName}
          onSave={onSave}
          onEnableLegacyBos={onEnableLegacyBos}
          onArchiveHostname={archiveHostnameAndGoBack}
          onTemporarilyDisableOrdering={onTemporarilyDisableOrdering}
          fetchTemporaryDisableText={fetchTemporaryDisableText}
        />
        {isBbotAdmin && hostnameConfig.configType !== "notConfigured" && (
          <LocationAssignmentSection
            key={hostnameConfig.hostname}
            hostname={hostnameConfig.hostname}
            locationsByCustomer={locationsByCustomer}
            customers={locationCustomers}
            fulfillmentMethods={fulfillmentMethods}
            prettyNames={prettyNames}
            isLoading={isLoadingLocations}
            isSaving={isSavingLocations}
            hideSaveControls={hostnameConfig.configType === "basicDeliveryTheme"}
            onSave={onSaveHostnameLocations}
          />
        )}
      </CardStack>
    </div>
  );
};

export default HostnameConfigDetail;
